export type TeamShortOptions = {
  /**
   * If true, the number will be placed before the team code (e.g., "1H" instead of "H1").
   * @default true
   */
  preferLeadingNumber?: boolean;
};

/**
 * Generates a concise abbreviation for a team name based on common patterns.
 *
 * This function handles various team name formats including:
 * - U-Teams (e.g., "U19" -> "U19")
 * - Old Boys / Veterans (e.g., "Alte Herren" -> "AH")
 * - Seniors (e.g., "Senioren" -> "S")
 * - Gender-specific teams (e.g., "Männer 1" -> "M1", "Damen 2" -> "D2")
 * - Youth classes (e.g., "A-Junioren" -> "AJ")
 *
 * @param input - The full team name to abbreviate.
 * @param opts - Configuration options for the abbreviation generation.
 * @returns The abbreviated team name or the original input if no pattern matches (or empty string if input is invalid).
 */
export function getAbbreviatedTeamName(
  input: string | undefined | null,
  opts: TeamShortOptions = {}
): string {
  if (!input?.trim()) return "";

  const preferLeadingNumber = opts.preferLeadingNumber ?? true;

  const raw = input.trim();
  // Normalize text: lowercase, remove dots/commas, normalize spaces
  const text = raw
    .toLowerCase()
    .replace(/[.,]/g, "")
    .replace(/\s+/g, " ")
    .trim();

  // Helper functions

  /**
   * Extracts the first number found in the text.
   */
  const firstNumber = (): string => {
    const m = text.match(/\b(\d+)\b/);
    return m ? m[1] : "";
  };

  /**
   * Formats the output code with the number based on preference.
   */
  const put = (code: string, num = ""): string =>
    preferLeadingNumber ? `${num}${code}` : `${code}${num}`;

  const num = firstNumber();

  // 1) U-Teams: U19 / U 19 / U-19
  //    Also tolerant towards "u19 junioren" etc.
  const uMatch = text.match(/\bu[\s-]?(\d{1,2})\b/);
  if (uMatch) {
    // For U-teams, typically Uxx is used without additional number
    return `U${uMatch[1]}`;
  }

  // 2) Old Boys / Veterans (Alte Herren / Alt-Herren / AH)
  if (/\b(alte|alt)\s*-?\s*herren\b|\bah\b/.test(text)) {
    // If input contains e.g. "Alte Herren 2" -> AH2 or 2AH depending on options
    return put("AH", num);
  }

  // 3) Seniors (optional differentiation, here "S")
  if (/\bsenior(en|innen)?\b/.test(text)) {
    return put("S", num);
  }

  // 4) Men/Women/Gender categories (+ synonyms)
  const categoryChecks: Array<[RegExp, string]> = [
    [/\bm(ä|ae)n(n)?er\b|\bmaenner\b|\bmen\b/, "M"],
    [/\bherren\b|\bmens\b/, "H"],
    [/\bdamen\b|\bwomen\b/, "D"],
    [/\bfrauen\b/, "F"],
  ];

  for (const [re, code] of categoryChecks) {
    if (re.test(text)) return put(code, num);
  }

  // 4b) Explicit "Mannschaft" / "Team" handling (e.g. "1. Mannschaft" -> "1M")
  if (/\b(mannschaft|team)\b/.test(text)) {
    return put("M", num);
  }

  // 5) Youth classes: A-Junioren, A Jugend, "F Jugend Mannschaft" etc.
  //    - Accepts "junioren", "jugend", optional "mannschaft/team"
  //    - Important: "F Jugend" is NOT Women (Frauen), but Youth Class F -> FJ
  const youthMatch = text.match(/\b([a-g])\b[\s-]*(junioren|jugend)\b/);
  if (youthMatch) {
    return `${youthMatch[1].toUpperCase()}J`;
  }

  // 6) If "Junioren" or "Jugend" exists but letter is missing:
  //    e.g. "Bambini" (though Bambini often implies G-Jugend, generic J fallback here) or "Jugend 2" -> J2
  if (/\b(junioren|jugend)\b/.test(text)) {
    return put("J", num);
  }

  // 7) Fallback: Number + first significant letter
  const stop = new Set([
    "mannschaft",
    "team",
    "jugend",
    "junioren",
    "die",
    "der",
    "des",
    "von",
    "und",
    "i",
    "ii",
    "iii",
  ]);

  const words = text.split(" ").filter((w) => w && !stop.has(w));
  if (words.length) {
    const first = words[0].replace(/[^a-zäöüß]/g, "");
    const letter = first ? first[0].toUpperCase() : "";
    if (letter) return put(letter, num);
  }

  // Last resort: return the raw input, but truncated if > 2 chars (fallback)
  return raw.length > 2 ? raw.substring(0, 2) : raw;
}
